import React, { useState, useEffect, useContext, useCallback, memo } from "react";
import CustomButton from "../../../components/controls/button/Button";
import {
  CalendarIcon,
  FilterCross,
  MeetingsClockIcon,
} from "../../../modules/default-svg";
import styles from "./ResourceMonitor.module.scss";
import Modal from "../../../components/controls/modal/Modal";
import Input from "../../../components/controls/input/customInput/CustomInput";
import {
  Radio,
  RadioGroup,
  Avatar,
  Button,
  TeachingPopover,
  TeachingPopoverBody,
  TeachingPopoverHeader,
  TeachingPopoverSurface,
  TeachingPopoverTrigger,
} from "@fluentui/react-components";

import { AddPersonIcon } from "../../../modules/default-svg";
import InputUnderline from "../../../components/controls/input/InputUnderline";
import ModalCheckbox from "../../../components/controls/modalcheckbox/ModalCheckbox";
import DayButton from "../../../components/dayButton/DayButton";
import Search from "../../../components/header/CustomSearch";
import SavedShift from "./SavedShift";
import SearchedResource from "./SearchedResource";
import ResourceMonitoringMain from "./Calandars/ResourceMoniteringMain";
import { ResourcePlannerContext } from "./ResourcePlannerContext";
import { getProjects } from "../../../store/actions/projects.actions";
import { projects } from "../../../store/selectors/projects.selector";
import { connect } from "react-redux";

const dayMapping = {
  Su: "Sunday",
  Mo: "Monday",
  Tu: "Tuesday",
  We: "Wednesday",
  Th: "Thursday",
  Fr: "Friday",
  Sa: "Saturday",
};

const ResourceMonitoring = memo((props) => {
  const {
    assignShift,
    setAssignShift,
    assignShiftRequest,
    allShifts,
    loading,
    dashboard = false,
    clickHandler = null,
    displayType,
    allProjects,
    getProjectsRequest,
  } = props;

  const permissions = localStorage.getItem("permissions");
  const [showModal, setShowModal] = useState(false);
  const [isdefault, setDefault] = useState(false);
  const [selectedResources, setSelectedResources] = useState([]);
  const [searchedResources, setSearchedResources] = useState([]);
  const [isPopoverOpen, setIsPopoverOpen] = useState(false);
  const [isDisable, setIsDisable] = useState(false);
  const [formErrors, setFormErrors] = useState({
    resources: "",
    name: "",
    endDate: "",
    shift: "",
  });

  const {
    assignedResources,
    setAssignedResources,
    setStartDate,
    startDate,
    endDate,
    setEndDate,
    selectedValue,
    setSelectedValue,
    selectedShift,
    setSelectedShift,
    name,
    setname,
    startTime,
    setStartTime,
    endTime,
    setEndTime,
    isDefault,
    setIsDefault,
    selectedDays,
    setSelectedDays,
    editShift,
    setEditShift,
    setAddShiftView,
    fetchCalendarData,
  } = useContext(ResourcePlannerContext);

  const dateFormatter = useCallback((d) => {
    const inputDate = new Date(d);
    const year = inputDate.getFullYear();
    const month = String(inputDate.getMonth() + 1).padStart(2, "0");
    const day = String(inputDate.getDate()).padStart(2, "0");
    return `${year}-${month}-${day}`;
  }, []);

  const handleSave = useCallback(async () => {
    setIsDisable(true);
    const errors = {};
    
    if (assignedResources.length === 0) {
      errors.resources = "Resources is a required field.";
    }
    if (startDate && endDate && startDate > endDate) {
      errors.endDate = "End date must be later than start date.";
    }
    if (!selectedShift && !name && !startTime && !endTime) {
      errors.shift = "Shift is a required field.";
    }
    
    setFormErrors(errors);
    if (Object.keys(errors).length) {
      setIsDisable(false);
      return;
    }

    const payload = {
      resources: assignedResources.map((resource) => resource.id),
      startdate: startDate ? dateFormatter(startDate) : null,
      enddate: endDate ? dateFormatter(endDate) : "",
      daysofweek: selectedDays,
      ...(selectedShift.length > 0 ? { shifts: [selectedShift] } : {}),
      ...(!selectedShift && name && startTime && endTime
        ? {
            newshift: {
              name,
              starttime: startTime,
              endTime: endTime,
              isDefault: isdefault,
            },
          }
        : {}),
    };

    try {
      await assignShiftRequest({ payload });
      await fetchCalendarData(false);
      
      setAddShiftView(false);
      setSelectedDays([]);
      setEndTime(null);
      setStartTime(null);
      setSelectedValue(null);
      setShowModal(false);
    } catch (error) {
      console.error("Failed to assign shift:", error);
    } finally {
      setIsDisable(false);
    }
  }, [
    assignedResources,
    startDate,
    endDate,
    selectedShift,
    name,
    startTime,
    endTime,
    selectedDays,
    isdefault,
    assignShiftRequest,
    fetchCalendarData,
    setAddShiftView,
    setSelectedDays,
    setEndTime,
    setStartTime,
    setSelectedValue,
    dateFormatter,
  ]);

  useEffect(() => {
    if (!loading) {
      setShowModal(false);
    }
  }, [loading]);

  const handleCancel = useCallback(() => {
    setShowModal(false);
    setSelectedDays([]);
    setAssignedResources([]);
    setSearchedResources([]);
    setFormErrors({});
    setStartDate(null);
    setEndDate(null);
    setSelectedValue(null);
  }, [setSelectedDays, setAssignedResources, setStartDate, setEndDate, setSelectedValue]);

  useEffect(() => {
    if (!allProjects.length) {
      getProjectsRequest({ page: 1, size: 10 });
    }
  }, [allProjects, getProjectsRequest]);

  const handleRadioChange = useCallback((value) => {
    if (value === "Saved Shift") {
      setSelectedValue((prevValue) =>
        prevValue === "Saved Shift" ? null : "Saved Shift"
      );
    } else {
      setSelectedValue(value);
    }
  }, [setSelectedValue]);

  const handleSavedShift = useCallback((e) => {
    setSelectedShift(e.target.value);
  }, [setSelectedShift]);

  const handleDayClick = useCallback((btnText) => {
    const fullDayName = dayMapping[btnText];
    setSelectedDays((prevSelectedDays) => {
      if (prevSelectedDays.includes(fullDayName)) {
        return prevSelectedDays.filter((day) => day !== fullDayName);
      } else {
        return [...prevSelectedDays, fullDayName];
      }
    });
  }, [setSelectedDays]);

  const handleAssigneResource = useCallback((resource) => {
    setAssignedResources((prevSelectedResource) => {
      if (prevSelectedResource.some((res) => res.id === resource.id)) {
        return prevSelectedResource.filter((res) => res.id !== resource.id);
      } else {
        return [...prevSelectedResource, { ...resource }];
      }
    });
  }, [setAssignedResources]);

  const handleSearchResult = useCallback((res) => {
    setSearchedResources(res);
  }, []);

  const handleAddResource = useCallback(() => {
    setAssignedResources((prevResources) => [
      ...prevResources,
      ...selectedResources,
    ]);
    setSelectedResources([]);
    setIsPopoverOpen(false);
    setSearchedResources([]);
    setFormErrors({});
  }, [selectedResources, setAssignedResources]);

  const handleClearSearchedData = useCallback(() => {
    setSearchedResources([]);
    setIsPopoverOpen(false);
  }, []);

  const handleSelectResource = useCallback((resource) => {
    setSelectedResources((prevSelected) => {
      if (prevSelected.includes(resource)) {
        return prevSelected.filter((res) => res !== resource);
      } else {
        return [...prevSelected, resource];
      }
    });
  }, []);

  useEffect(() => {
    if (startDate && endDate && startDate > endDate) {
      setFormErrors((prevErrors) => ({
        ...prevErrors,
        endDate: "End date must be later than start date",
      }));
    } else {
      setFormErrors((prevErrors) => ({
        ...prevErrors,
        endDate: "",
      }));
    }
  }, [startDate, endDate]);

  const formBody = useCallback(() => {
    return (
      <>
        {showModal && (
          <div className="mb-2">
            <h5 className="text-md-500 mb-3">Assign to</h5>
            <div
              className={`AssignResBox ${
                assignedResources.length > 0
                  ? "assignToWrapper"
                  : "selectResource"
              }`}
            >
              <div className="assignToItems">
                <TeachingPopover
                  positioning="below-start"
                  withArrow={false}
                  open={isPopoverOpen}
                  onOpenChange={(event, data) => setIsPopoverOpen(data.open)}
                >
                  <TeachingPopoverTrigger>
                    <Button className="assignAvatarBtn">
                      <Avatar icon={<AddPersonIcon />} name="" />
                      <p className="text-xsm-400 ms-1 d-flex align-items-center">
                        Assign resource<span className="Input_staric">*</span>
                      </p>
                    </Button>
                  </TeachingPopoverTrigger>
                  <TeachingPopoverSurface className="assignAvatarSurface">
                    <TeachingPopoverHeader
                      className="assignAvatarHeader"
                      icon={false}
                    >
                      Resources
                    </TeachingPopoverHeader>
                    <TeachingPopoverBody className="assignAvatarBody">
                      <Search
                        parentType="Shift"
                        type="Resource"
                        searchResult={handleSearchResult}
                      />
                      <div className="mt-2 userChipWrapper">
                        {searchedResources
                          .filter(
                            (item) =>
                              !assignedResources
                                .map((data) => data.id)
                                .includes(item.id)
                          )
                          .map((res) => (
                            <SearchedResource
                              key={res.id}
                              item={res}
                              isActive={selectedResources.includes(res)}
                              onSelect={() => handleSelectResource(res)}
                            />
                          ))}
                      </div>
                    </TeachingPopoverBody>
                    <div className="assignAvatarFooter">
                      <CustomButton
                        btnClass="d_btn d_btn_whiteXsm me-10"
                        label="Cancel"
                        clickHandler={handleClearSearchedData}
                      />
                      <CustomButton
                        btnClass="d_btn d_btn_primaryXsm"
                        label="Add Resources"
                        clickHandler={handleAddResource}
                      />
                    </div>
                  </TeachingPopoverSurface>
                </TeachingPopover>
              </div>
              {assignedResources.map((res) => (
                <div className="assignToItems" key={res.id}>
                  <Avatar
                    color="colorful"
                    name={res.fullName}
                    image={{ src: "" }}
                  />
                  <p className="text-xsm-400">{res.fullName}</p>
                  <div onClick={() => handleAssigneResource(res)}>
                    <FilterCross />
                  </div>
                </div>
              ))}
            </div>
            {formErrors.resources && (
              <p className="Input_errorMessage">{formErrors.resources}</p>
            )}
          </div>
        )}

        <div className="mb-4">
          <h5 className="text-md-500 mb-3">Apply to period</h5>
          <div className="dayWrapper d-flex column-gap-3">
            {Object.keys(dayMapping).map((shortName) => (
              <DayButton
                key={shortName}
                btnText={shortName}
                btnClass="LargeDayBtn"
                onDayClick={handleDayClick}
                selectedDays={selectedDays}
              />
            ))}
          </div>
        </div>
        <div className="d-flex align-items-start w-100 mb-4">
          <Input
            inputwd="w-50 me-3"
            icon={<CalendarIcon />}
            value={startDate ? startDate : ""}
            type="date"
            handleChange={setStartDate}
            label="Start Date"
            placeholder="Enter Date"
          />
          <Input
            inputwd="w-50"
            icon={<CalendarIcon />}
            value={endDate ? endDate : ""}
            type="date"
            handleChange={setEndDate}
            label="End Date"
            placeholder="Enter Date"
            errorMessage={formErrors.endDate}
          />
        </div>
        <h5 className="text-md-500 mb-3">
          Select shift<span className="Input_staric">*</span>
        </h5>
        <RadioGroup
          layout="horizontal"
          className="mb-3"
          value={selectedValue}
          onChange={(e) => handleRadioChange(e.target.value)}
        >
          <Radio
            className="radioWrapper"
            value="Saved Shift"
            label="Saved Shift"
          />
          {permissions.includes("Shift.Create") && (
            <Radio
              className="radioWrapper"
              value="Create new Shift"
              label="Create new Shift"
            />
          )}
        </RadioGroup>
        {selectedValue === "Saved Shift" && (
          <RadioGroup
            onChange={handleSavedShift}
            value={selectedShift}
            className="d-block"
          >
            <div className={`${styles.selectShiftWrap} row g-2`}>
              {allShifts &&
                allShifts.map((shift) => <SavedShift item={shift} />)}
            </div>
          </RadioGroup>
        )}
        {selectedValue === "Create new Shift" && (
          <div className={styles.selectShiftWrap}>
            <div className="d-flex align-items-start w-100 mb-3">
              <InputUnderline
                inputUnderlinewd="w-100"
                placeholder="Shift name"
                value={name}
                handleChange={setname}
                error={formErrors.name}
              />
            </div>
            <div
              className={`${styles.selectShiftInput} d-flex align-items-start w-100 mb-4 `}
            >
              <Input
                inputwd="w-50 me-3"
                label="Start Time"
                icon={<MeetingsClockIcon />}
                placeholder="00:00"
                value={startTime}
                type="time"
                handleChange={setStartTime}
              />
              <Input
                inputwd="w-50"
                label="End Time"
                icon={<MeetingsClockIcon />}
                placeholder="00:00"
                value={endTime}
                type="time"
                handleChange={setEndTime}
                errorMessage={formErrors.endTime}
              />
            </div>
            <div className="d-flex align-items-start">
              <ModalCheckbox
                label="Mark as Default"
                checked={isDefault}
                setChecked={() => {
                  setIsDefault(!isDefault);
                  setDefault(!isdefault);
                }}
              />
            </div>
          </div>
        )}
        {!showModal && (
          <div className="d-flex align-items-center justify-content-end mt-3">
            <CustomButton
              btnClass="d_btn d_btn_primarySm font-weight-500"
              label="Save Shift"
              isDisabled={isDisable}
              clickHandler={handleSave}
            />
          </div>
        )}
        {formErrors.shift && (
          <p className="Input_errorMessage">{formErrors.shift}</p>
        )}
      </>
    );
  }, [
    showModal,
    assignedResources,
    isPopoverOpen,
    searchedResources,
    selectedResources,
    formErrors,
    isDisable,
    handleSearchResult,
    handleSelectResource,
    handleClearSearchedData,
    handleAddResource,
    handleAssigneResource,
    handleSave,
  ]);

  useEffect(() => {
    if (assignShift) {
      setShowModal(true);
      setAssignShift(false);
      setAssignedResources([]);
      setStartDate("");
      setEndDate("");
    }
  }, [assignShift, setAssignShift, setAssignedResources, setStartDate, setEndDate]);

  return (
    <div className={styles.ResMonitoringContainer}>
      <ResourceMonitoringMain
        formBody={formBody}
        dashboard={dashboard}
        clickHandler={clickHandler}
        displayType={displayType}
      />
      {showModal && (
        <Modal
          title={"Assign Shift"}
          handleCancel={handleCancel}
          handleSave={handleSave}
          saveBtnLabel="Save"
          cancelBtnLabel="Cancel"
          isLoading={loading}
          size="large"
          body={formBody()}
          zIndex={1}
        />
      )}
    </div>
  );
});

ResourceMonitoring.displayName = 'ResourceMonitoring';

const mapDispatchToProps = {
  getProjectsRequest: getProjects,
};

const mapStateToProps = (state) => {
  const allProjects = projects(state);
  return { allProjects };
};

export default connect(mapStateToProps, mapDispatchToProps)(ResourceMonitoring);
